import { Box, Divider, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';

import { Declaration, DeclarationCustomsType, DeclarationErrorType, DeclarationStatus, EnvironmentCountries } from '@e-origin/shared';

import { useDeclarationsErrorDescriptions } from '@hooks/declaration';
import { useSettings } from '@hooks/useSettings';
import { classes as globalClasses } from '@styles';
import { renderField } from './DeclarationViewCustoms';
import classes from './DeclarationViewCustoms.module.css';

interface DeclarationViewRejectionsProps {
  declaration: Declaration;
}

const DECLARATION_ERROR_TYPE_LABELS: Record<DeclarationErrorType, string> = {
  [DeclarationErrorType.FUNCTIONAL_REJECTION]: 'Functional error',
  [DeclarationErrorType.TECHNICAL_REJECTION]: 'Technical error',
  [DeclarationErrorType.WARNING]: 'Validation warning',
};

export const DeclarationViewRejections: React.FC<DeclarationViewRejectionsProps> = ({ declaration }) => {
  const { data: settings } = useSettings();
  const { data: errorTexts = {} } = useDeclarationsErrorDescriptions(declaration.customsState?.functionalError || []);

  const showContent = useMemo(() => {
    return (
      declaration.generalInfo?.status === DeclarationStatus.INVALIDATED ||
      ((declaration.customsState?.sentTo !== DeclarationCustomsType.PLDA || declaration.generalInfo.status === DeclarationStatus.REJECTED) &&
        (!!declaration.customsState?.customsDeclarationRejection || declaration.customsState?.functionalError?.length > 0))
    );
  }, [declaration]);

  const isFr = settings?.country === EnvironmentCountries.FR;

  if (!showContent) {
    return;
  }

  return (
    <Stack mb="xl">
      {(declaration.customsState?.sentTo !== DeclarationCustomsType.PLDA || declaration.generalInfo.status === DeclarationStatus.REJECTED) && (
        <>
          <Stack mt={16}>
            {declaration.customsState?.customsDeclarationRejection && (
              <Stack>
                <Group className={classes.businessRejectionRow}>
                  {renderField('Business rejection type', declaration.customsState?.customsDeclarationRejection.businessRejectionType)}
                </Group>
                <Group className={classes.businessRejectionRow}>
                  {renderField('Rejection date', declaration.customsState?.customsDeclarationRejection.rejectionDate)}

                  {declaration.customsState?.customsDeclarationRejection.rejectionCode &&
                    renderField('Rejection code', declaration.customsState?.customsDeclarationRejection.rejectionCode)}
                  {declaration.customsState?.customsDeclarationRejection.rejectionReason &&
                    renderField('Rejection reason', declaration.customsState?.customsDeclarationRejection.rejectionReason)}
                </Group>
              </Stack>
            )}
            {declaration.customsState?.functionalError?.length > 0 && (
              <Stack>
                {declaration.customsState?.functionalError.map((item, itemIdx) => (
                  <Box key={itemIdx}>
                    <Divider />
                    <Stack key={`functional-error-${item.sequenceNumber}`} mt={itemIdx > 0 ? 'xl' : ''} gap="xs">
                      <Text className={classes.functionalErrorTitle}>{(item.type && DECLARATION_ERROR_TYPE_LABELS[item.type]) || 'Functional error'}</Text>

                      <Group className={classes.functionalErrorItem}>
                        {item.sequenceNumber && renderField('Sequence number', item.sequenceNumber)}

                        {renderField('Error code', item.errorCode)}
                      </Group>
                      {item.errorPointer && (
                        <Stack>
                          <label>Error pointer</label>
                          {(isFr ? [item.errorPointer.trim()] : item.errorPointer.trim().split(/\s+/)).map((pointer) => (
                            <p key={pointer}>{pointer}</p>
                          ))}
                        </Stack>
                      )}
                      <Group className={classes.functionalErrorItem}>{renderField('Error reason', item.errorReason)}</Group>
                      {errorTexts[item.sequenceNumber]?.system && errorTexts[item.sequenceNumber].system !== '' && (
                        <Group className={classes.functionalErrorItem}>{renderField('System Description', errorTexts[item.sequenceNumber].system)}</Group>
                      )}
                      {errorTexts[item.sequenceNumber]?.declaration && errorTexts[item.sequenceNumber].declaration !== '' && (
                        <Group className={classes.functionalErrorItem}>
                          {renderField('Declaration / Legal Description', errorTexts[item.sequenceNumber].declaration)}
                        </Group>
                      )}

                      {!!item.remarks?.length && item.remarks !== 'N/A' && (
                        <Group className={classes.functionalErrorItem}>{renderField('Remarks', item.remarks)}</Group>
                      )}
                      {!!item.originalAttributeValue?.length && (
                        <Group className={classes.functionalErrorItem}>{renderField('Original Attribute', item.originalAttributeValue)}</Group>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </>
      )}
      {declaration.generalInfo?.status === DeclarationStatus.INVALIDATED && (
        <>
          <Group className={classes.invalidationDecisionStatusRow}>
            {renderField('Invalidation decision date', declaration.customsState?.invalidationDecision?.invalidationDecisionDate)}
            {renderField(
              'Invalidation request date',
              declaration.customsState?.invalidationDecision?.invalidationRequestDate || declaration.customsState?.invalidationRequest?.invalidationRequestDate,
            )}
            {renderField(
              'Invalidation initiated by customs',
              declaration.customsState?.invalidationDecision?.invalidationInitiatedByCustoms === 1 ? 'yes' : 'no',
            )}
          </Group>
          <Box className={classes.invalidationRequestForm} mt="30">
            <label>Invalidation reason</label>
            <textarea
              className={globalClasses.inputBorder}
              value={declaration.customsState?.invalidationDecision?.invalidationReason || declaration.customsState?.invalidationRequest?.invalidationReason}
              disabled
            />
          </Box>
        </>
      )}
    </Stack>
  );
};
